<template>
  <IonPageContainer>
    <TheAlertPage
      imageName="check-internet-connection.svg"
      alertTitle="No internet connection"
      alertMessage="Check your connection and try again."
      alertIcon="wifi-off"
      alertColor="warning"
    >
      <template #additionalContent>
        <CustomIonButton
          color="primary"
          :isDisabled="isRetrying"
          class="mt-3"
          @onClick="retryConnection"
        >
          <CustomRotatingIcon v-if="isRetrying" color="light" />
          <span class="ms-2">{{ isRetrying ? "Retrying..." : "Try Again" }}</span>
        </CustomIonButton>
        <CustomIonButton
          v-if="IsLoggedIn && !IsStudent"
          color="tertiary"
          class="mt-2"
          @onClick="gotoOfflineDownloads"
        >
          <i class="ti ti-cloud-download me-1"></i>
          <span>Go to Downloads</span>
        </CustomIonButton>
      </template>
    </TheAlertPage>
  </IonPageContainer>
</template>
<script lang="ts">
import IonPageContainer from "@/components/layout/IonPageContainer.vue";
import CustomIonButton from "@/components/primitives/CustomIonButton.vue";
import CustomRotatingIcon from "@/components/primitives/CustomRotatingIcon.vue";
import TheAlertPage from "@/components/states/TheAlertPage.vue";
import { useAppNavigation } from "@/methods/utils";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useSessionStore } from "@/stores/core/sessionStore";
import { storeToRefs } from "pinia";
import { defineComponent, ref, watch } from "vue";
import { useRoute } from "vue-router";

export default defineComponent({
  name: "CheckInternetConnection",
  components: {
    IonPageContainer,
    CustomIonButton,
    TheAlertPage,
    CustomRotatingIcon,
  },
  setup() {
    const { navigateTo } = useAppNavigation();
    const route = useRoute();
    const isRetrying = ref(false);
    const sessionStore = useSessionStore();
    const { IsLoggedIn, IsStudent } = storeToRefs(sessionStore);
    const appSettingsStore = useAppSettingsStore();
    const { IsOnline } = storeToRefs(appSettingsStore);

    // Auto-retry the moment connectivity returns. This watches the store instead of a raw window
    // `online` event because that event is not reliable inside a Capacitor WebView — on Android
    // and iOS the reconnect is reported by @capacitor/network, so the native app used to sit on
    // this screen until the user tapped Try Again.
    //
    // The route check is load-bearing. navigateTo pushes, and Ionic keeps a pushed-from page
    // MOUNTED (only `ion-page-hidden`) in the outlet's view stack, so this watcher stays live
    // behind whatever the user moved on to. Without the check, the next connectivity blip drags
    // them off that screen back to LandingPage. useRoute() reports the GLOBAL current route —
    // it keeps updating inside a cached page, which is exactly what is needed here.
    watch(IsOnline, (isOnline) => {
      if (isOnline && route.name === "CheckInternetConnection") {
        retryConnection();
      }
    });

    // Re-read the OS status before navigating. The router guard reads IsOnline and would bounce
    // straight back here if it were stale — which is exactly what happens when the flag was set
    // false by a failed request and the OS never emitted the networkStatusChange that would clear
    // it. Without this the button would look alive and do nothing.
    async function retryConnection() {
      if (isRetrying.value) return;
      isRetrying.value = true;
      await appSettingsStore.refreshNetworkStatus();
      navigateTo({ name: "LandingPage" });
      isRetrying.value = false;
    }

    function gotoOfflineDownloads() {
      navigateTo({ name: "OfflineDownloads" });
    }

    return { IsLoggedIn, IsStudent, isRetrying, retryConnection, gotoOfflineDownloads };
  },
});
</script>
